<template>
  <div class="recovery-page" :class="`recovery-period-${period}`" :style="backgroundStyle">
    <div class="recovery-language">
      <vue-feather type="globe"></vue-feather>
      <select :value="currentLocale" @change="changeLanguage($event.target.value)">
        <option value="fr">Français</option>
        <option value="en">English</option>
        <option value="ar">العربية</option>
      </select>
      <vue-feather type="chevron-down"></vue-feather>
    </div>

    <main class="recovery-card">
      <img class="recovery-logo" :src="logoUrl" :alt="companyName" @error="logoUrl = defaultLogoUrl" />
      
      <h1>{{ $t(isReset ? 'passwordRecovery.resetTitle' : 'passwordRecovery.forgotTitle') }}</h1>
      <p>{{ $t(isReset ? 'passwordRecovery.resetIntro' : 'passwordRecovery.forgotIntro') }}</p>

      <form @submit.prevent="submit">
        <div class="recovery-field">
          <label>{{ $t('login.email') }}</label>
          <div class="recovery-input">
            <input v-model.trim="form.email" type="email" autocomplete="email" required
              :readonly="isReset && Boolean($route.query.email)" />
            <span class="recovery-field-icon" aria-hidden="true"><vue-feather type="mail"></vue-feather></span>
          </div>
        </div>

        <template v-if="isReset">
          <div class="recovery-field">
            <label>{{ $t('passwordRecovery.newPassword') }}</label>
            <div class="recovery-input">
              <input v-model="form.password" :type="showPassword ? 'text' : 'password'"
                autocomplete="new-password" minlength="8" required />
              <button type="button" @click="showPassword = !showPassword" :aria-label="$t('login.password')">
                <vue-feather :type="showPassword ? 'eye' : 'eye-off'"></vue-feather>
              </button>
            </div>
          </div>
          <div class="recovery-field">
            <label>{{ $t('passwordRecovery.confirmPassword') }}</label>
            <div class="recovery-input">
              <input v-model="form.password_confirmation" :type="showPassword ? 'text' : 'password'"
                autocomplete="new-password" minlength="8" required />
              <span class="recovery-field-icon" aria-hidden="true"><vue-feather type="check-circle"></vue-feather></span>
            </div>
          </div>
        </template>

        <div v-if="errorMessage" class="recovery-error">{{ errorMessage }}</div>
        <div v-if="successMessage" class="recovery-success">{{ successMessage }}</div>

        <button class="recovery-submit" type="submit" :disabled="loading || Boolean(successMessage)">
          <span v-if="loading" class="spinner-border spinner-border-sm"></span>
          <span>{{ $t(loading ? 'passwordRecovery.sending' : (isReset ? 'passwordRecovery.resetSubmit' : 'passwordRecovery.sendSubmit')) }}</span>
          <vue-feather v-if="!loading" type="arrow-right"></vue-feather>
        </button>
      </form>

      <router-link class="recovery-back" to="/">
        <vue-feather type="arrow-left"></vue-feather>
        {{ $t('passwordRecovery.backLogin') }}
      </router-link>
    </main>
  </div>
</template>

<script>
import { i18n, setAppLocale } from "@/i18n";
import { getPublicBranding } from "@/services/companyService";
import { requestPasswordReset, resetPassword } from "@/services/authService";

export default {
  data() {
    return {
      companyName: "Business Manager",
      defaultLogoUrl: require("@/assets/img/logo.png"),
      logoUrl: require("@/assets/img/logo.png"),
      loading: false,
      showPassword: false,
      errorMessage: "",
      successMessage: "",
      period: "day",
      timer: null,
      backgrounds: {
        morning: require("@/assets/img/login-backgrounds/morning.jpg"),
        day: require("@/assets/img/login-backgrounds/day.jpg"),
        sunset: require("@/assets/img/login-backgrounds/sunset.jpg"),
        night: require("@/assets/img/login-backgrounds/night.jpg"),
      },
      form: { email: "", password: "", password_confirmation: "" },
    };
  },
  computed: {
    currentLocale() { return i18n.global.locale.value; },
    isReset() { return this.$route.name === "reset-password"; },
    backgroundStyle() {
      return { backgroundImage: `linear-gradient(rgba(7,25,42,.32), rgba(7,25,42,.48)), url(${this.backgrounds[this.period]})` };
    },
  },
  mounted() {
    this.form.email = String(this.$route.query.email || "");
    this.updatePeriod();
    this.timer = window.setInterval(this.updatePeriod, 60000);
    this.loadBranding();
  },
  beforeUnmount() {
    if (this.timer) window.clearInterval(this.timer);
  },
  methods: {
    changeLanguage(locale) { setAppLocale(locale); },
    updatePeriod() {
      const hour = new Date().getHours();
      this.period = hour >= 5 && hour < 10 ? "morning" : hour >= 10 && hour < 17 ? "day" : hour >= 17 && hour < 20 ? "sunset" : "night";
    },
    normalizeStorageUrl(path) {
      if (!path) return null;
      if (/^(https?:\/\/|data:image\/)/i.test(path)) return path;
      const base = (process.env.VUE_APP_URL || window.location.origin).replace(/\/$/, "");
      return `${base}/storage/${path.replace(/^\/?storage\//, "")}`;
    },
    async loadBranding() {
      try {
        const response = await getPublicBranding();
        const branding = response?.data || {};
        this.companyName = branding.name || this.companyName;
        this.logoUrl = this.normalizeStorageUrl(branding.logo) || this.defaultLogoUrl;
      } catch (_) { this.logoUrl = this.defaultLogoUrl; }
    },
    extractError(error) {
      const errors = error?.response?.data?.errors;
      if (errors) {
        const first = Object.values(errors).flat()[0];
        if (first) return first;
      }
      return error?.response?.data?.message || this.$t("passwordRecovery.genericError");
    },
    async submit() {
      this.errorMessage = "";
      this.successMessage = "";
      if (this.isReset && this.form.password !== this.form.password_confirmation) {
        this.errorMessage = this.$t("passwordRecovery.passwordMismatch");
        return;
      }
      this.loading = true;
      try {
        if (this.isReset) {
          await resetPassword({ ...this.form, token: this.$route.params.token });
          this.successMessage = this.$t("passwordRecovery.resetSuccess");
          window.setTimeout(() => this.$router.push("/"), 1800);
        } else {
          await requestPasswordReset(this.form.email);
          this.successMessage = this.$t("passwordRecovery.sentSuccess");
        }
      } catch (error) {
        this.errorMessage = this.extractError(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.recovery-page{align-items:center;background-position:center;background-size:cover;display:flex;justify-content:center;min-height:100vh;padding:28px;position:relative}.recovery-language{align-items:center;-webkit-backdrop-filter:blur(18px);backdrop-filter:blur(18px);background:rgba(255,255,255,.62);border:1px solid rgba(255,255,255,.7);border-radius:12px;display:flex;gap:8px;inset-inline-end:24px;padding:7px 10px;position:absolute;top:24px}.recovery-language svg{height:16px;width:16px}.recovery-language select{appearance:none;background:transparent;border:0;color:#173653;font-size:13px;font-weight:650;outline:0;padding:4px 16px}.recovery-card{-webkit-backdrop-filter:blur(30px) saturate(150%);backdrop-filter:blur(30px) saturate(150%);background:linear-gradient(145deg,rgba(255,255,255,.46),rgba(255,255,255,.24));border:1px solid rgba(255,255,255,.68);border-radius:24px;box-shadow:0 30px 80px rgba(5,25,43,.34);max-width:460px;padding:42px 46px;text-align:center;width:100%}.recovery-logo{max-height:58px;max-width:200px;object-fit:contain}.recovery-icon{align-items:center;background:rgba(255,159,67,.16);border-radius:14px;color:#e97910;display:flex;height:48px;justify-content:center;margin:24px auto 18px;width:48px}.recovery-icon svg{height:22px;width:22px}.recovery-card h1{color:#113250;font-size:27px;font-weight:750;margin:0 0 10px}.recovery-card>p{color:#536a7e;font-size:14px;line-height:1.6;margin:0 auto 25px;max-width:360px}.recovery-field{margin-bottom:16px;text-align:start}.recovery-field label{color:#243e57;display:block;font-size:13px;font-weight:650;margin-bottom:7px}.recovery-input{position:relative}.recovery-input input{background:rgba(255,255,255,.48);border:1px solid rgba(255,255,255,.65);border-radius:11px;color:#142d46;height:49px;outline:0;padding-inline:14px 48px;width:100%}.recovery-input input:focus{background:rgba(255,255,255,.7);border-color:#ff9f43;box-shadow:0 0 0 4px rgba(255,159,67,.14)}.recovery-input>svg,.recovery-input button{color:#75899c;inset-inline-end:10px;position:absolute;top:50%;transform:translateY(-50%)}.recovery-input>svg{height:18px;width:18px}.recovery-input button{align-items:center;background:none;border:0;display:flex;height:34px;justify-content:center;width:34px}.recovery-input button svg{height:18px;width:18px}.recovery-submit{align-items:center;background:#ff9f43;border:0;border-radius:11px;box-shadow:0 12px 24px rgba(255,159,67,.28);color:#fff;display:flex;font-weight:700;gap:9px;height:50px;justify-content:center;margin-top:8px;width:100%}.recovery-submit:disabled{cursor:not-allowed;opacity:.7}.recovery-submit svg{height:17px;width:17px}.recovery-error,.recovery-success{border-radius:9px;font-size:13px;margin:12px 0;padding:10px 12px;text-align:start}.recovery-error{background:rgba(220,53,69,.12);color:#a72836}.recovery-success{background:rgba(25,135,84,.13);color:#176a45}.recovery-back{align-items:center;color:#294f70;display:inline-flex;font-size:13px;font-weight:650;gap:7px;margin-top:22px;text-decoration:none}.recovery-back svg{height:16px;width:16px}.recovery-period-night .recovery-card{background:linear-gradient(145deg,rgba(8,34,61,.66),rgba(11,43,70,.44));border-color:rgba(180,218,245,.38);box-shadow:0 34px 90px rgba(0,10,24,.52)}.recovery-period-night .recovery-card h1,.recovery-period-night .recovery-field label{color:#f4f9fd}.recovery-period-night .recovery-card>p{color:rgba(225,239,249,.76)}.recovery-period-night .recovery-input input{background:rgba(242,248,253,.9);border-color:rgba(220,237,249,.72)}.recovery-period-night .recovery-back{color:#b9ddf5}.recovery-period-night .recovery-language{background:rgba(8,34,61,.62);border-color:rgba(190,220,241,.34);color:#f2f8fc}.recovery-period-night .recovery-language select{color:#f2f8fc}.recovery-period-night .recovery-language option{color:#173653}:global(html[dir="rtl"]) .recovery-submit svg,:global(html[dir="rtl"]) .recovery-back svg{transform:rotate(180deg)}@media(max-width:575px){.recovery-page{padding:16px}.recovery-language{inset-inline-end:14px;top:14px}.recovery-card{padding:38px 24px}.recovery-card h1{font-size:24px}}
.recovery-field-icon{align-items:center;color:#75899c;display:flex;height:34px;inset-inline-end:10px;justify-content:center;pointer-events:none;position:absolute;top:50%;transform:translateY(-50%);width:34px}.recovery-field-icon svg{height:18px;width:18px}.recovery-period-night .recovery-card{background:linear-gradient(145deg,rgba(8,34,61,.72),rgba(11,43,70,.55))!important;border-color:rgba(180,218,245,.42)!important}.recovery-period-night .recovery-card h1,.recovery-period-night .recovery-field label{color:#f4f9fd!important}.recovery-period-night .recovery-card>p{color:rgba(225,239,249,.84)!important}.recovery-period-night .recovery-back{color:#b9ddf5!important}.recovery-period-night .recovery-icon{background:rgba(255,159,67,.2);color:#ffad5d}.recovery-page:dir(rtl) .recovery-submit svg,.recovery-page:dir(rtl) .recovery-back svg{transform:rotate(180deg)}
</style>
